import * as React from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { searchTagNumber } from '../../../services/scaffoldRequestService'
import type { FormField } from '../../../types/Form/FormDefinition'

type TagNumberFieldProps = {
	field: FormField
	projectId: number
	value: string
	error?: string
	onChange: (value: string) => void
}

export default function TagNumberField({
	field,
	projectId,
	value,
	error,
	onChange,
}: TagNumberFieldProps) {
	const [options, setOptions] = React.useState<string[]>([])
	const [loading, setLoading] = React.useState(false)
	const [open, setOpen] = React.useState(false)

	const searchTagNumbers = React.useCallback(
		async (searchText: string) => {
			if (!searchText.trim() || searchText.length < 2) {
				setOptions([])
				return
			}
			try {
				setLoading(true)
				const results = await searchTagNumber(searchText, projectId)
				setOptions(results.map((tag) => tag.toString()))
			} catch (error) {
				console.error('Error searching tag numbers:', error)
				setOptions([])
			} finally {
				setLoading(false)
			}
		},
		[projectId],
	)

	const debouncedSearchTagNumbers = React.useMemo(() => {
		let timeoutId: ReturnType<typeof setTimeout>
		return (searchText: string) => {
			clearTimeout(timeoutId)
			timeoutId = setTimeout(() => searchTagNumbers(searchText), 300)
		}
	}, [searchTagNumbers])

	const onTagNumberInput = (raw: string) => {
		onChange(raw)
		debouncedSearchTagNumbers(raw)
		if (raw) {
			setOpen(true)
		} else {
			setOpen(false)
			setOptions([])
		}
	}

	return (
		<Autocomplete
			freeSolo
			disableClearable
			options={options}
			value={value}
			onChange={(_, newValue) => onTagNumberInput(newValue != null ? String(newValue) : '')}
			onInputChange={(_, newInputValue) => onTagNumberInput(newInputValue)}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			loading={loading}
			renderInput={(params) => (
				<TextField
					{...params}
					label={field.label}
					required={field.isRequired}
					error={Boolean(error)}
					helperText={error ?? field.helpText ?? 'Enter the existing scaffold tag number'}
					variant='outlined'
					size='small'
					fullWidth
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? (
									<CircularProgress color='inherit' size={20} sx={{ m: 0, p: 0 }} />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
					sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
				/>
			)}
		/>
	)
}
